import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AppData } from '../helpers/app-data';
import { 
  PlantillaDocumento, 
  PlantillaDocumentoRequest, 
  PlantillaDocumentoUpdateRequest,
  PlantillaDocumentoResponse,
  PlantillaDocumentoListResponse 
} from '../models/plantilla.model';

@Injectable({
  providedIn: 'root'
})
export class PlantillasService {

  public baseUrl: string;

  constructor(private http: HttpClient) {
    this.baseUrl = AppData.API_URL + 'PlantillaDocumento';
  }

  // Obtener todas las plantillas
  getPlantillas(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/ListaPlantillasDocumento`);
  }

  // Obtener plantillas activas
  getPlantillasActivas(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/ListaPlantillasActivas`);
  }

  // Obtener plantilla por ID
  getPlantillaPorId(id: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/DescargarPlantilla?id=${id}`);
  }

  // Crear plantilla con archivo base64
  crearPlantilla(plantilla: PlantillaDocumentoRequest): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/CrearPlantilla`, plantilla);
  }

  // Convertir archivo a base64
  convertirArchivoABase64(archivo: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Eliminar el prefijo data:... si existe
        const base64Data = result.includes(',') ? result.split(',')[1] : result;
        resolve(base64Data);
      };
      reader.onerror = error => reject(error);
      reader.readAsDataURL(archivo);
    });
  }

  // Crear plantilla completa (convierte archivo a base64 y envía todo junto)
  async crearPlantillaCompleta(nombre: string, descripcion: string, archivo: File): Promise<Observable<any>> {
    try {
      const archivoBase64 = await this.convertirArchivoABase64(archivo);
      const extension = archivo.name.split('.').pop()?.toUpperCase() || '';
      
      const plantillaRequest: PlantillaDocumentoRequest = {
        nombre,
        descripcion,
        nombreArchivo: archivo.name,
        extension,
        archivoBase64,
        activo: true
      };

      return this.crearPlantilla(plantillaRequest);
    } catch (error) {
      throw new Error('Error al procesar el archivo');
    }
  }

  // Eliminar plantilla
  eliminarPlantilla(id: number): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/EliminarPlantilla?id=${id}`);
  }

  // Descargar plantilla
  descargarPlantilla(id: number): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/DescargarPlantilla?id=${id}`, { 
      responseType: 'blob' 
    });
  }

  // Obtener plantilla por ID para edición
  obtenerPlantilla(id: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/ObtenerPlantilla?id=${id}`);
  }

  // Editar plantilla (con o sin archivo)
  editarPlantilla(request: any): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/EditarPlantilla`, request);
  }

  // Obtener URL para visualización de documento
  obtenerUrlVisualizacion(id: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/ObtenerUrlVisualizacion?id=${id}`);
  }

  // Obtener datos para DataTable
  getDataTablePlantillas(request: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/GetDataTablePlantillasDocumento`, request);
  }
}